// sync.ts — client side of the Phase-3 data backend (server/src/routes/data.js).
//
// Three jobs, all fail-soft (a sync error never blocks the app):
//   1. Progress  — pull on sign-in + merge, push (debounced) on change. Operational
//                  sync; runs whenever the user is signed in with a real token.
//   2. Lesson feedback / Act surveys — posted on submit, CONSENT-GATED by the caller.
//                  Queued locally and retried if offline.
//
// PRIVACY: only operational journey fields are ever sent (see toSyncBlob). Free-text
// the user wrote — reflections, intentions, CARE actions, review notes — NEVER leaves
// the device (DPIA: journals/special-category data are on-device only). The server
// stores everything encrypted at rest in durable Postgres, keyed by an HMAC of the
// Supabase user id.

import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { BACKEND_URL, FOGS_CHECK_URL, RESEARCH_CHECKIN_URL, RESEARCH_PROGRESS_URL, RESEARCH_MEASURE_URL, RESEARCH_MINE_URL, ORG_REDEEM_URL, RESEARCH_CONSENT_URL, RESEARCH_DELETE_URL, BILLING_PORTAL_URL, KIT_FORGET_URL } from '@/config/backend';
import { SUPABASE } from '@/config/supabase';
import { storage } from './storage';
import type { ProgressState } from './progress';
import { freshAccessToken, type AuthSession } from './auth';
import { trackEvent } from './metrics';
import { WEB_RETURN_URL, latestAt, type FogsOpenMode, type FogsStoredMode } from './fogs';

const api = (path: string) => `${BACKEND_URL.replace(/\/$/, '')}${path}`;
const QUEUE_KEY = 'renurture.sync.queue.v1';

/** A real, server-verifiable session: signed in AND not the offline local trial. */
export function canSync(session: AuthSession | null): session is AuthSession {
  return !!session && !!session.accessToken && session.accessToken !== 'local';
}

function authHeaders(token: string): Record<string, string> {
  return { authorization: `Bearer ${token}`, 'content-type': 'application/json' };
}

// ── the operational subset that is safe to sync (no free-text the user wrote) ──
/** Journey/progress fields only. Deliberately EXCLUDES reflections, intentions,
 *  careActions, reviewComments, guide impressions, mood — anything free-text or
 *  device-only. Adding a field here means it leaves the device, so add with care. */
export interface SyncBlob {
  completedSteps: string[];
  completedModules: string[];
  completedAt: Record<string, string>;
  currentStepId: string | null;
  positions: Record<string, number>;
  favourites: string[];
  practisedSteps: string[];
  practiceDays: Record<string, string[]>;
  presenceDays: string[];
  actBoundariesSeen: string[];
  activeProgramme: ProgressState['activeProgramme'];
  onboardingDone: boolean;
  // Self-soothing "moments of practice" counts (Calm Your Body + Toolkit uses).
  // Plain integers, no free-text — safe to sync, so they survive a reinstall /
  // new device the same way completed steps do. They only ever grow (merge = max).
  calmSessionsCount: number;
  toolUsesCount: number;
  // FOGS full check: themes + flagged section keys only (never answers); small flags.
  needsThemes?: ProgressState['needsThemes'];
  fogsResult?: ProgressState['fogsResult'];
  fogsOpenedAt?: string | null;
  fogsOpenedMode?: FogsStoredMode | null;
  fogsDismissedBeforeAct1?: boolean;
  fogsActReminderDone?: boolean;
  fogsDoneLocal?: boolean;
}

export function toSyncBlob(s: ProgressState): SyncBlob {
  return {
    completedSteps: s.completedSteps,
    completedModules: s.completedModules,
    completedAt: s.completedAt,
    currentStepId: s.currentStepId,
    positions: s.positions,
    favourites: s.favourites,
    practisedSteps: s.practisedSteps,
    practiceDays: s.practiceDays,
    presenceDays: s.presenceDays,
    actBoundariesSeen: s.actBoundariesSeen,
    activeProgramme: s.activeProgramme,
    onboardingDone: s.onboardingDone,
    calmSessionsCount: s.calmSessionsCount ?? 0,
    toolUsesCount: s.toolUsesCount ?? 0,
    needsThemes: s.needsThemes ?? null,
    fogsResult: s.fogsResult ?? null,
    fogsOpenedAt: s.fogsOpenedAt ?? null,
    fogsOpenedMode: s.fogsOpenedMode ?? null,
    fogsDismissedBeforeAct1: s.fogsDismissedBeforeAct1 ?? false,
    fogsActReminderDone: s.fogsActReminderDone ?? false,
    fogsDoneLocal: s.fogsDoneLocal ?? false,
  };
}

const uniq = (a: string[] = [], b: string[] = []) => Array.from(new Set([...a, ...b]));

/** Merge a remote blob into the local one. Progress only ever moves forward, so we
 *  UNION step/day arrays and keep the larger play position — a conservative merge
 *  that never loses a completion regardless of which device wrote last. */
export function mergeBlobs(local: SyncBlob, remote: Partial<SyncBlob> | null): SyncBlob {
  if (!remote) return local;
  const positions: Record<string, number> = { ...remote.positions, ...local.positions };
  for (const k of Object.keys(remote.positions ?? {})) {
    positions[k] = Math.max(remote.positions?.[k] ?? 0, local.positions[k] ?? 0);
  }
  const practiceDays: Record<string, string[]> = { ...remote.practiceDays };
  for (const k of uniq(Object.keys(local.practiceDays), Object.keys(remote.practiceDays ?? {}))) {
    practiceDays[k] = uniq(local.practiceDays[k], remote.practiceDays?.[k]);
  }
  return {
    completedSteps: uniq(local.completedSteps, remote.completedSteps),
    completedModules: uniq(local.completedModules, remote.completedModules),
    completedAt: { ...remote.completedAt, ...local.completedAt },
    currentStepId: local.currentStepId ?? remote.currentStepId ?? null,
    positions,
    favourites: uniq(local.favourites, remote.favourites),
    practisedSteps: uniq(local.practisedSteps, remote.practisedSteps),
    practiceDays,
    presenceDays: uniq(local.presenceDays, remote.presenceDays),
    actBoundariesSeen: uniq(local.actBoundariesSeen, remote.actBoundariesSeen),
    activeProgramme: local.activeProgramme,
    onboardingDone: local.onboardingDone || !!remote.onboardingDone,
    // Counts only grow — keep the larger of the two sides.
    calmSessionsCount: Math.max(local.calmSessionsCount ?? 0, remote.calmSessionsCount ?? 0),
    toolUsesCount: Math.max(local.toolUsesCount ?? 0, remote.toolUsesCount ?? 0),
    needsThemes: latestAt(local.needsThemes, remote.needsThemes),
    fogsResult: latestAt(local.fogsResult, remote.fogsResult),
    ...(remote.fogsOpenedAt && (!local.fogsOpenedAt || Date.parse(remote.fogsOpenedAt) > Date.parse(local.fogsOpenedAt))
      ? { fogsOpenedAt: remote.fogsOpenedAt, fogsOpenedMode: remote.fogsOpenedMode ?? null }
      : { fogsOpenedAt: local.fogsOpenedAt ?? null, fogsOpenedMode: local.fogsOpenedMode ?? null }),
    // Dismissals only ever move forward.
    fogsDismissedBeforeAct1: !!local.fogsDismissedBeforeAct1 || !!remote.fogsDismissedBeforeAct1,
    fogsActReminderDone: !!local.fogsActReminderDone || !!remote.fogsActReminderDone,
    fogsDoneLocal: !!local.fogsDoneLocal || !!remote.fogsDoneLocal,
  };
}

// ── progress network ─────────────────────────────────────────────────────────
/** Pull the user's remote progress blob. Returns { data, version } or null on failure. */
export async function pullProgress(token: string): Promise<{ data: Partial<SyncBlob> | null; version: number } | null> {
  try {
    const r = await fetch(api('/api/progress'), { headers: authHeaders(token) });
    if (!r.ok) return null;
    const json = await r.json();
    return { data: json.data ?? null, version: Number(json.version) || 0 };
  } catch {
    return null;
  }
}

/** True when a `pullProgress()` result actually REACHED the server — i.e. it is safe
 *  to mark the token "pulled" and allow pushes. `null` means the pull FAILED (offline /
 *  401 / 5xx) and the server state is UNKNOWN, so the caller must NOT push or an empty/
 *  stale local could overwrite a healthy server (the progress-reset data-loss bug). A
 *  SUCCESSFUL pull of an empty server is `{ data: null }` and returns true (a genuinely
 *  new user has nothing to clobber). Extracted as a pure predicate so the clobber guard
 *  in useSync is unit-tested. */
export function pullReachedServer(remote: { data: Partial<SyncBlob> | null; version: number } | null): boolean {
  return remote !== null;
}

/** Push the user's progress blob. `version` is a monotonically increasing counter. */
export async function pushProgress(token: string, blob: SyncBlob, version: number): Promise<boolean> {
  try {
    const r = await fetch(api('/api/progress'), {
      method: 'PUT',
      headers: authHeaders(token),
      body: JSON.stringify({ data: blob, version }),
    });
    return r.ok;
  } catch {
    return false;
  }
}

// ── research data: queued posts (consent-gated by the caller) ─────────────────
type QueuedPost =
  | { kind: 'lesson'; stepId: string; sentiment: string; note?: string }
  | { kind: 'survey'; actId: string; scores: Record<string, number> };

function loadQueue(): QueuedPost[] {
  return storage.getJSON<QueuedPost[]>(QUEUE_KEY, []);
}
function saveQueue(q: QueuedPost[]): void {
  storage.setJSON(QUEUE_KEY, q.slice(-100)); // bounded — never grow without limit
}

async function sendPost(token: string, p: QueuedPost): Promise<boolean> {
  const path = p.kind === 'lesson' ? '/api/feedback/lesson' : '/api/feedback/survey';
  const body = p.kind === 'lesson' ? { stepId: p.stepId, sentiment: p.sentiment, note: p.note } : { actId: p.actId, scores: p.scores };
  try {
    const r = await fetch(api(path), { method: 'POST', headers: authHeaders(token), body: JSON.stringify(body) });
    return r.ok;
  } catch {
    return false;
  }
}

/** Try to send a research post now; on failure queue it for the next flush. The
 *  CALLER is responsible for only invoking this when the user has consented. */
export async function postResearch(token: string, p: QueuedPost): Promise<void> {
  const ok = await sendPost(token, p);
  if (!ok) saveQueue([...loadQueue(), p]);
}

export const queueLessonFeedback = (token: string, stepId: string, sentiment: string, note?: string) =>
  postResearch(token, { kind: 'lesson', stepId, sentiment, note: note?.trim() || undefined });

export const queueSurvey = (token: string, actId: string, scores: Record<string, number>) =>
  postResearch(token, { kind: 'survey', actId, scores });

/** Mirror the end-of-act wellbeing-6 into the PID-linked research store (D1
 *  impact_measures), so a consented user's act surveys sit alongside their check-in
 *  measures for longitudinal analysis. This is IN ADDITION to queueSurvey (the app's
 *  own encrypted copy keyed by user_key) — the two stores are deliberately separate.
 *  Uses the same instrument + pid-bound token as the web check-in. The CALLER must
 *  check research consent. Fully fail-soft. */
export async function pushResearchActSurvey(token: string, actId: string, scores: Record<string, number>): Promise<void> {
  try {
    const res = await fetchResearchPid(token);
    if (!res?.pid) return;
    await fetch(RESEARCH_MEASURE_URL, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        pid: res.pid,
        token: res.appToken,
        cohort: res.cohort || '',
        instrument: 'wellbeing-6-v1',
        occasion: actId, // act1 | act2 | act3 — distinguishes act surveys from check-ins
        scores,
      }),
    });
  } catch {
    /* research save is best-effort; the app-store copy (queueSurvey) is the source of truth for the user */
  }
}

// ── off-app research check-in (longitudinal impact; pid-linked) ───────────────
/** The server's /api/research/pid ALWAYS responds 200 with `{ pid, cohort, token }`
 *  — `pid` is only ever `null` in the body for a CONFIRMED reason (a known
 *  non-survivor; see server/src/routes/data.js). So a non-2xx response, a thrown
 *  fetch error, or a failed token refresh is never "confirmed no pid" — it means
 *  we simply couldn't find out. `fetchResearchPidResult` keeps those states apart;
 *  `fetchResearchPid` (below) is the fail-soft wrapper every other caller already
 *  uses, for which "couldn't find out" and "confirmed none" are both just null. */
export type ResearchPidResult =
  | { status: 'ok'; pid: string; cohort: string | null; appToken: string | null }
  | { status: 'none' } // server responded ok: this user genuinely has no pid
  | { status: 'error' }; // couldn't determine — network/non-2xx/refresh failure

export async function fetchResearchPidResult(token: string): Promise<ResearchPidResult> {
  try {
    // Use a freshly-refreshed token (the passed one may be a stale cached session
    // token, which Replit would 401 — leaving the check-in with no pid). Kept inside
    // the try: a rejected refresh must resolve to "error", never throw to the caller.
    const live = (await freshAccessToken()) || token;
    const r = await fetch(api('/api/research/pid'), { headers: authHeaders(live) });
    if (!r.ok) return { status: 'error' };
    const j = await r.json();
    return typeof j.pid === 'string' && j.pid
      ? { status: 'ok', pid: j.pid, cohort: j.cohort ?? null, appToken: typeof j.token === 'string' ? j.token : null }
      : { status: 'none' };
  } catch {
    return { status: 'error' };
  }
}

/** Mint the user's anonymised research participant id (+ cohort) from the backend.
 *  Returns null if signed-out/offline/unconfigured OR if this user genuinely has
 *  no pid — every existing caller here is fail-soft and treats both the same way.
 *  `deleteAccount`'s research wipe needs to tell those apart, so it calls
 *  `fetchResearchPidResult` directly instead. */
export async function fetchResearchPid(
  token: string,
): Promise<{ pid: string; cohort: string | null; appToken: string | null } | null> {
  const res = await fetchResearchPidResult(token);
  return res.status === 'ok' ? { pid: res.pid, cohort: res.cohort, appToken: res.appToken } : null;
}

/** Open the web wellbeing check-in, carrying the anonymised pid (+ cohort) so a
 *  person's check-ins link over time. Opening never hard-fails: if the pid can't
 *  be fetched, the page still opens (it shows an "open from the app" note). The
 *  survey lives off-app on purpose — no in-app survey schemas to maintain. */
export async function openResearchCheckin(
  token: string,
  occasion?: string,
  opts?: { firstCheckin?: boolean; consent?: boolean },
): Promise<void> {
  // Best-effort: failing to mint the pid (or refresh the token) must NEVER stop the
  // check-in opening — the page handles a missing pid with an "open from the app"
  // note. Without this guard a rejected token refresh aborted before Browser.open,
  // making the "Set my starting point" button a silent dead tap.
  // On web, the in-app browser ultimately calls window.open — but we have to
  // await the pid first, by which point the click gesture has lapsed and pop-up
  // blockers silently swallow it (the old "Set my starting point does nothing"
  // bug, web edition). So on web we open a blank tab NOW, inside the gesture, and
  // redirect it once the URL is built. Native keeps the in-app browser.
  const isWeb = !Capacitor.isNativePlatform();
  const preWin = isWeb ? window.open('about:blank', '_blank') : null;
  let res: { pid: string; cohort: string | null; appToken: string | null } | null = null;
  try {
    res = await fetchResearchPid(token);
  } catch {
    res = null;
  }
  const u = new URL(RESEARCH_CHECKIN_URL);
  if (res?.pid) u.searchParams.set('pid', res.pid);
  if (res?.cohort) u.searchParams.set('cohort', res.cohort);
  // The pid-bound token authenticates the submission to /research-suite so a
  // forged pid can't pollute the longitudinal data (null until APP_PAGE_SECRET set).
  if (res?.appToken) u.searchParams.set('token', res.appToken);
  if (occasion) u.searchParams.set('occasion', occasion);
  // First check-in only: ask the one-time demographics survey ("who are we
  // supporting?"). The page stores it write-once per pid, and routes anyone still
  // in the relationship to crisis support instead of the programme.
  if (opts?.firstCheckin) u.searchParams.set('demographics', '1');
  // The check-in is the user's own outcome tracking (ROM); the page keeps the
  // result for the user regardless, but only treats it as research data when the
  // user has consented. Pass the current consent state through so the page knows.
  u.searchParams.set('consent', opts?.consent ? '1' : '0');
  // Tell the survey page it was opened in a browser tab so it shows its own
  // "Return to ReNurture" button (there's no native Done/X chrome on web).
  if (isWeb) u.searchParams.set('platform', 'web');
  if (isWeb) {
    if (preWin) {
      preWin.opener = null; // sever the opener link now that it points to our own page
      preWin.location.href = u.toString();
    } else {
      // A blocker still intervened despite the in-gesture open — navigate in place.
      window.location.href = u.toString();
    }
    return;
  }
  await Browser.open({ url: u.toString() });
}

/** Open the full FOGS self-check (renurture.org/assessment-fogs) from the app. On web adds
 *  return=<the live web app> so the page's "Return to ReNurture" hands back only the flagged
 *  section keys. For every signed-in user (consented or not) a PID + token is minted when possible
 *  and carried as pid/cohort/token, with consent=1|0, so the page saves the answers like the check-in.
 *  The page is token-gated: with no PID/token (signed out, failed mint) nothing is opened and it
 *  resolves 'failed'.
 *  Resolves with how it opened, so callers can record whether a result can come back. */
export async function openFogsCheck(
  _origin: 'waiting' | 'onboarding' | 'progress',
  opts?: { token?: string | null; consent?: boolean },
): Promise<FogsOpenMode> {
  const isWeb = !Capacitor.isNativePlatform();
  // Same web pop-up handling as openResearchCheckin: open the tab inside the click gesture.
  const preWin = isWeb ? window.open('about:blank', '_blank') : null;
  trackEvent('open', 'screen', 'fogs-open');
  let res: { pid: string; cohort: string | null; appToken: string | null } | null = null;
  if (opts?.token) {
    try { res = await fetchResearchPid(opts.token); } catch { res = null; }
  }
  if (!res?.pid || !res.appToken) {
    preWin?.close();
    return 'failed';
  }
  const u = new URL(FOGS_CHECK_URL);
  // Web only: the page navigates back with done=1&flagged=. Native uses the in-app browser's
  // Done/X (no custom-scheme return); results come back through research-mine for linked users.
  if (isWeb) u.searchParams.set('return', WEB_RETURN_URL);
  u.searchParams.set('pid', res.pid);
  if (res.cohort) u.searchParams.set('cohort', res.cohort);
  u.searchParams.set('token', res.appToken);
  // Like the wellbeing check-in: the page saves for the user either way and treats it as
  // research data only when they have consented.
  u.searchParams.set('consent', opts?.consent ? '1' : '0');
  // Same as the wellbeing check-in: platform=web only on web (native keeps the in-app browser).
  if (isWeb) u.searchParams.set('platform', 'web');
  const mode: FogsOpenMode = isWeb ? 'web' : 'linked';
  if (isWeb) {
    if (preWin) {
      preWin.opener = null;
      preWin.location.href = u.toString();
    } else {
      window.location.href = u.toString();
    }
    return mode;
  }
  await Browser.open({ url: u.toString() });
  return mode;
}

/** Open the Stripe Billing Customer Portal (view invoices, update card, CANCEL a
 *  subscription). POSTs the Supabase token to /billing-portal and opens the
 *  returned Stripe URL. On web we pre-open a tab inside the click gesture (the
 *  POST is async, so a pop-up blocker would otherwise swallow it). Returns an
 *  error string the caller can surface (e.g. "no subscription found"). */
export async function openBillingPortal(token: string): Promise<{ ok: boolean; error?: string }> {
  const isWeb = !Capacitor.isNativePlatform();
  const preWin = isWeb ? window.open('about:blank', '_blank') : null;
  try {
    const r = await fetch(BILLING_PORTAL_URL, {
      method: 'POST',
      headers: { authorization: `Bearer ${token}` },
    });
    const d = (await r.json().catch(() => null)) as { url?: string; error?: string } | null;
    if (!r.ok || !d?.url) {
      if (preWin) preWin.close();
      return { ok: false, error: d?.error || 'Could not open the billing portal.' };
    }
    if (isWeb) {
      if (preWin) { preWin.opener = null; preWin.location.href = d.url; }
      else window.location.href = d.url;
    } else {
      await Browser.open({ url: d.url });
    }
    return { ok: true };
  } catch {
    if (preWin) preWin.close();
    return { ok: false, error: 'Network error. Please check your connection and try again.' };
  }
}

/** Record a research-consent change on the server so its record matches the app's
 *  state. POSTs { pid, token, consent } to RESEARCH_CONSENT_URL. Best-effort: a
 *  failure must never break the consent UI (the app's local state is authoritative). */
export async function postResearchConsent(token: string, consent: boolean): Promise<void> {
  try {
    const res = await fetchResearchPid(token);
    if (!res?.pid) return;
    await fetch(RESEARCH_CONSENT_URL, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ pid: res.pid, token: res.appToken, consent }),
    });
  } catch {
    /* fail-soft */
  }
}

// ── own check-in history (the in-app "Your progress" view) ───────────────────
export interface MyProgressWave { wave: string; composite: number | null; at?: string | null; /** fogs-v1 only: flagged section keys. */ flagged?: string[] }
export interface MyProgressInstrument {
  id: string;
  label: string;
  higherIsBetter: boolean;
  mixed: boolean;
  min: number;
  max: number;
  waves: MyProgressWave[];
}

/** Fetch the signed-in user's OWN check-in history (the 3 measures over time).
 *  Mints the pid+token, then GETs /research-mine. Returns null on any problem
 *  (offline, signed-out, not configured) so the screen can degrade gently. */
export async function fetchMyProgress(token: string): Promise<MyProgressInstrument[] | null> {
  const res = await fetchResearchPid(token);
  if (!res?.pid) return null;
  try {
    const u = new URL(RESEARCH_MINE_URL);
    u.searchParams.set('pid', res.pid);
    if (res.appToken) u.searchParams.set('token', res.appToken);
    const r = await fetch(u.toString());
    if (!r.ok) return null;
    const j = await r.json();
    return j && j.ok && Array.isArray(j.instruments) ? (j.instruments as MyProgressInstrument[]) : null;
  } catch {
    return null;
  }
}

// ── organisation access redemption (B2B/EAP) ─────────────────────────────────
/** Redeem organisation access for the signed-in user. With a `code` it grants via
 *  that code; with an empty string it asks the backend to match the user's VERIFIED
 *  email domain to a bought org licence. The Worker writes membership directly +
 *  tags the cohort; the caller should then recheck entitlement. Fully fail-soft. */
export type OrgUnavailableReason = 'seats_full' | 'expired' | 'cancelled';
export async function redeemOrgCode(
  token: string,
  code: string,
): Promise<{ granted: boolean; error?: string; noDomainMatch?: boolean; termMonths?: number; orgName?: string; reason?: OrgUnavailableReason }> {
  try {
    const r = await fetch(ORG_REDEEM_URL, {
      method: 'POST',
      headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
      body: JSON.stringify({ code: code || '' }),
    });
    const d = (await r.json().catch(() => null)) as
      | { granted?: boolean; already?: boolean; error?: string; noDomainMatch?: boolean; term_months?: number; org?: string; reason?: string }
      | null;
    if (r.ok && d && (d.granted || d.already)) return { granted: true, termMonths: d.term_months, orgName: d.org || undefined };
    // A `reason` means the org was RECOGNISED (real code or matched domain) but the
    // seat can't be granted (spent / expired / cancelled) — distinct from a bad code
    // (no reason) or a non-org user (noDomainMatch).
    const reason = (['seats_full', 'expired', 'cancelled'] as const).find((x) => x === d?.reason);
    return {
      granted: false,
      error: (d && d.error) || `Could not redeem (${r.status}).`,
      noDomainMatch: !!(d && d.noDomainMatch),
      orgName: (d && d.org) || undefined,
      reason,
    };
  } catch {
    return { granted: false, error: 'Network error — please try again.' };
  }
}

// ── account deletion (GDPR erasure / Apple 5.1.1) ────────────────────────────
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Run `fn` up to `attempts` times with a short linear backoff between tries,
 *  stopping at the first success. Network blips shouldn't block a legitimate
 *  deletion, but a step that keeps failing must NOT be treated as done. */
async function withRetries(fn: () => Promise<boolean>, attempts = 3, backoffMs = 300): Promise<boolean> {
  for (let i = 0; i < attempts; i++) {
    if (await fn()) return true;
    if (i < attempts - 1) await sleep(backoffMs * (i + 1));
  }
  return false;
}

async function wipeServerProgress(token: string): Promise<boolean> {
  try {
    const r = await fetch(api('/api/account'), { method: 'DELETE', headers: authHeaders(token) });
    return r.ok;
  } catch {
    return false;
  }
}

async function wipeResearchData(token: string, researchConsented: boolean): Promise<boolean> {
  const res = await fetchResearchPidResult(token);
  // A CONFIRMED "this user has no pid" (server responded ok, pid genuinely null) —
  // nothing to erase, not a failure. Anything else (network error, non-2xx, a
  // failed token refresh) means we simply don't know, and must NOT be treated as
  // success — the caller (deleteAccount, via withRetries) retries this whole
  // function, so returning false here just makes it try again.
  if (res.status === 'none') return true;
  if (res.status === 'error') return false;
  try {
    // Pass the user's CURRENT research consent. If they consented, the endpoint
    // retains the (now-anonymous) research dataset; if not, it erases every
    // research row. So account deletion wipes everything EXCEPT consented research.
    const r = await fetch(RESEARCH_DELETE_URL, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ pid: res.pid, token: res.appToken, consent: researchConsented }),
    });
    return r.ok;
  } catch {
    return false;
  }
}

/** Permanently delete the signed-in user's account. Purges the server's user-keyed
 *  data first (while the token is valid), then the D1 research/ROM rows, then deletes
 *  the Supabase auth user (which cascade-removes profiles + seat bookings). Steps 1
 *  and 2 are retried a couple of times on failure (a network blip shouldn't block a
 *  legitimate deletion) but if either still fails, the login is deliberately NOT
 *  deleted — deleting the login first would strand the user with no way to prove who
 *  they are and retry, leaving orphaned data on the server forever. Returns ok only if
 *  every step actually succeeded. The caller should sign out + clear local data on
 *  success, and show the returned error (and let the user retry) on failure. End
 *  state on success: Supabase app schema + auth/profile + D1 research rows are all
 *  purged. */
export async function deleteAccount(token: string, researchConsented = false): Promise<{ ok: boolean; error?: string }> {
  // 1. Purge server-side data (progress, feedback, surveys, cohort). Must succeed
  //    before we touch the login.
  const progressWiped = await withRetries(() => wipeServerProgress(token));
  if (!progressWiped) {
    return {
      ok: false,
      error: 'We could not delete your saved progress from our servers, so your account has not been deleted. Please check your connection and try again.',
    };
  }

  // 2. Purge the user's D1 research/ROM rows. Must happen BEFORE the Supabase auth
  //    delete so the pid-bound token is still mintable + valid — and must also
  //    succeed before we touch the login, for the same reason as step 1.
  const researchWiped = await withRetries(() => wipeResearchData(token, researchConsented));
  if (!researchWiped) {
    return {
      ok: false,
      error: 'We could not delete your research data from our servers, so your account has not been deleted. Please check your connection and try again.',
    };
  }

  // 2b. Unsubscribe from Kit (removes them from all mailing; GDPR erasure → processor).
  //     Best-effort, BEFORE the auth delete so the Worker can resolve the email from the
  //     still-valid token. A failure here never blocks account deletion — it is a
  //     marketing-list courtesy, not user data that would be orphaned.
  try {
    await fetch(KIT_FORGET_URL, { method: 'POST', headers: authHeaders(token) });
  } catch (e) {
    console.warn('[deleteAccount] kit unsubscribe failed (continuing):', e);
  }

  // 3. Delete the Supabase auth user — only reached once steps 1 and 2 have
  //    genuinely succeeded.
  try {
    const r = await fetch(`${SUPABASE.url.replace(/\/$/, '')}/functions/v1/delete-account`, {
      method: 'POST',
      headers: { authorization: `Bearer ${token}`, apikey: SUPABASE.anonKey, 'content-type': 'application/json' },
    });
    const d = (await r.json().catch(() => null)) as { ok?: boolean; error?: string } | null;
    if (r.ok && d?.ok) return { ok: true };
    return { ok: false, error: (d && d.error) || `Could not delete your account (${r.status}).` };
  } catch {
    return { ok: false, error: 'Network error. Please check your connection and try again.' };
  }
}

/** Cancel the signed-in user's Stripe subscription at the end of the paid period (the
 *  `cancel-subscription` edge function). Never cancels immediately; access runs on until
 *  `accessUntil`. The error string is the server's code (e.g. 'no_active_subscription')
 *  or a readable message the caller can map. */
export async function cancelStripeSubscription(token: string): Promise<{ ok: boolean; accessUntil?: string | null; error?: string }> {
  try {
    const r = await fetch(`${SUPABASE.url.replace(/\/$/, '')}/functions/v1/cancel-subscription`, {
      method: 'POST',
      headers: { authorization: `Bearer ${token}`, apikey: SUPABASE.anonKey, 'content-type': 'application/json' },
      body: JSON.stringify({ action: 'cancel' }),
    });
    const d = (await r.json().catch(() => null)) as { ok?: boolean; accessUntil?: string | null; error?: string } | null;
    if (r.ok && d?.ok) return { ok: true, accessUntil: d.accessUntil ?? null };
    return { ok: false, error: d?.error || `Could not cancel your subscription (${r.status}).` };
  } catch {
    return { ok: false, error: 'Network error. Please check your connection and try again.' };
  }
}

export interface StripeSubscriptionStatus {
  hasRecurring: boolean;
  cancelAtPeriodEnd: boolean;
  accessUntil: string | null;
}

/** Read-only check of the caller's Stripe subscription (the `cancel-subscription` edge
 *  function in status mode). Returns null on any failure so the UI can fall back. */
export async function getStripeSubscriptionStatus(token: string): Promise<StripeSubscriptionStatus | null> {
  try {
    const r = await fetch(`${SUPABASE.url.replace(/\/$/, '')}/functions/v1/cancel-subscription`, {
      method: 'POST',
      headers: { authorization: `Bearer ${token}`, apikey: SUPABASE.anonKey, 'content-type': 'application/json' },
      body: JSON.stringify({ action: 'status' }),
    });
    const d = (await r.json().catch(() => null)) as Partial<StripeSubscriptionStatus> | null;
    if (!r.ok || !d || typeof d.hasRecurring !== 'boolean') return null;
    return { hasRecurring: d.hasRecurring, cancelAtPeriodEnd: d.cancelAtPeriodEnd === true, accessUntil: d.accessUntil ?? null };
  } catch {
    return null;
  }
}

// ── PID-linked engagement snapshot (corporate/cohort reporting) ───────────────
const PROGRESS_PUSH_KEY = 'renurture.research.progress.v1';

/** Counts-only engagement summary from journey state — no content ever included. */
function progressMetrics(s: ProgressState) {
  return {
    activeProgramme: s.activeProgramme,
    lessonsCompleted: s.completedSteps?.length ?? 0,
    modulesCompleted: s.completedModules?.length ?? 0,
    actsSeen: s.actBoundariesSeen?.length ?? 0,
    daysPresent: s.presenceDays?.length ?? 0,
    onboardingDone: !!s.onboardingDone,
    // Self-soothing engagement: Calm Your Body sessions + Toolkit tool uses.
    calmSessions: s.calmSessionsCount ?? 0,
    toolUses: s.toolUsesCount ?? 0,
  };
}

/** Push the engagement snapshot to the research backend (counts only), keyed by the
 *  anonymised pid. Throttled to once per day; the CALLER must check research consent.
 *  Fully fail-soft — a network error just means it retries next session. */
export async function pushResearchProgress(token: string, state: ProgressState): Promise<void> {
  const today = new Date().toISOString().slice(0, 10);
  if (storage.get(PROGRESS_PUSH_KEY) === today) return; // at most once a day
  const res = await fetchResearchPid(token);
  if (!res?.pid) return;
  try {
    const r = await fetch(RESEARCH_PROGRESS_URL, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ pid: res.pid, cohort: res.cohort || '', token: res.appToken, metrics: progressMetrics(state) }),
    });
    if (r.ok) storage.set(PROGRESS_PUSH_KEY, today); // only stamp on success, so a failure retries
  } catch {
    /* fail-soft */
  }
}

// useSync.ts owns the progress version counter; name it here so a sign-out can
// wipe every sync artefact in one call (keep in step with useSync.VERSION_KEY).
const VERSION_KEY = 'renurture.sync.version.v1';

/** Clear all device-local sync state. Called on sign-out so the NEXT person on a
 *  shared device never inherits the previous user's queued posts or sync counters.
 *  (Progress + reflections themselves are cleared separately via ProgressStore.reset.) */
export function clearSyncState(): void {
  storage.remove(QUEUE_KEY);
  storage.remove(PROGRESS_PUSH_KEY);
  storage.remove(VERSION_KEY);
}

/** Drain the offline queue (called on sign-in / app foreground). Re-queues anything
 *  that still fails, so nothing is lost across sessions. */
export async function flushQueue(token: string): Promise<void> {
  const pending = loadQueue();
  if (!pending.length) return;
  saveQueue([]); // optimistically clear; failed sends are re-added below
  const stillFailing: QueuedPost[] = [];
  for (const p of pending) {
    const ok = await sendPost(token, p);
    if (!ok) stillFailing.push(p);
  }
  if (stillFailing.length) saveQueue([...loadQueue(), ...stillFailing]);
}
